import type { Home, Subscription } from 'core/services/HomeSlice';

// Matches avid-warranty-mvp's checkHomeKeepSubscription (core/helpers.ts) so both
// apps agree on who is a HomeKeep customer vs. a warranty-only customer.
export const WARRANTY_SUBSCRIPTION_NAME = 'AvidWarranty';

const isCurrent = (sub: Subscription, now: Date): boolean =>
  sub.active && new Date(sub.expires) > now;

const allSubscriptions = (homes: Home[] | null | undefined): Subscription[] =>
  (homes ?? []).flatMap((home) => home.subscriptions ?? []);

/** True when any home has an active, unexpired non-warranty subscription. */
export const hasHomeKeepAccess = (
  homes: Home[] | null | undefined,
  now: Date = new Date()
): boolean =>
  allSubscriptions(homes).some(
    (sub) => sub.name !== WARRANTY_SUBSCRIPTION_NAME && isCurrent(sub, now)
  );

/** True when any home has an active, unexpired AvidWarranty subscription. */
export const hasActiveWarranty = (
  homes: Home[] | null | undefined,
  now: Date = new Date()
): boolean =>
  allSubscriptions(homes).some(
    (sub) => sub.name === WARRANTY_SUBSCRIPTION_NAME && isCurrent(sub, now)
  );

interface WarrantyOnlyInput {
  homes: Home[] | null | undefined;
  isAdmin: boolean | null | undefined;
  warrantySlug: string | null | undefined;
  now?: Date;
}

/**
 * Warranty-only users belong on Avid Warranty, not HomeKeep. Only redirect when
 * there is somewhere to send them (active warranty + builder slug); super-admins
 * (including while impersonating) are never redirected.
 */
export const shouldRedirectToWarranty = ({
  homes,
  isAdmin,
  warrantySlug,
  now = new Date()
}: WarrantyOnlyInput): boolean =>
  !isAdmin &&
  !!warrantySlug &&
  hasActiveWarranty(homes, now) &&
  !hasHomeKeepAccess(homes, now);

/** Joins the warranty base URL and builder slug with exactly one slash. */
export const buildWarrantyUrl = (
  base: string,
  slug: string,
  linkToken?: string | null
): string => {
  const url = `${base.replace(/\/+$/, '')}/${slug.replace(/^\/+/, '')}`;
  return linkToken ? `${url}?link_token=${encodeURIComponent(linkToken)}` : url;
};
